import React, { useState, useEffect, useRef } from 'react';
import { Input, Tooltip, Popconfirm, Message } from '@arco-design/web-react';
import { IconDelete } from '@arco-design/web-react/icon';
import { AccountManager, SavedAccount, AccountType } from '@/utils/accountManager';
import { RefInputType } from '@arco-design/web-react/es/Input';
import './AccountSelector.less';

interface AccountSelectorProps {
  value?: string;
  onChange?: (value: string, password?: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  accountType?: AccountType;
}

const AccountSelector: React.FC<AccountSelectorProps> = ({
  value,
  onChange,
  placeholder = '请输入或选择账号',
  style,
  disabled = false,
  accountType = AccountType.USER
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState<SavedAccount[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<RefInputType>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const latestAccounts = AccountManager.getAllAccounts(accountType);
    
    if (latestAccounts.length > 0) {
      const sortedAccounts = [...latestAccounts].sort((a, b) => b.last_login_time - a.last_login_time);
      setSuggestions(sortedAccounts);
    }
  }, [accountType]);

  const handleSearch = (keyword: string) => {
    const results = AccountManager.searchAccounts(keyword, accountType);
    setSuggestions(results);
    setShowTooltip(results.length > 0);
  };

  const handleSelect = (account: SavedAccount) => {
    setInputValue(account.phone_number);
    onChange?.(account.phone_number, account.password);
    setShowTooltip(false);
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    onChange?.(newValue);
    handleSearch(newValue);
  };

  const handleInputFocus = () => {
    const latestAccounts = AccountManager.getAllAccounts(accountType);
    if (latestAccounts.length > 0 && !disabled) {
      setSuggestions(latestAccounts.sort((a, b) => b.last_login_time - a.last_login_time));
      setShowTooltip(true);
    }
  };

  const handleDeleteAccount = (accountId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    AccountManager.deleteAccount(accountId, accountType);
    setSuggestions(AccountManager.getAllAccounts(accountType));
    Message.success('账号已删除');
    setShowTooltip(false);
  };

  return (
    <div className="account-selector" style={style} ref={containerRef}>
      <Tooltip
        className="account-selector-tip"
        popupVisible={showTooltip}
        position="bottom"
        color="#fff"
        getPopupContainer={() => inputRef.current?.dom?.parentElement || document.body}
        content={<div className="account-tooltip">
            {suggestions.map((account) => (
              <div
                key={account.id}
                className="account-option"
                onClick={() => handleSelect(account)}
              >
                <div className="account-info">
                  <div className="account-phone">{account.phone_number}</div>
                  <div className="account-meta">
                    <span className="last-login">
                      最近登录: {new Date(account.last_login_time).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <IconDelete onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteAccount(account.id, e as any);
                }} style={{ color: 'red'}} />
              </div>
            ))}
          </div>}
      >
        <Input
          className="login-input"
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          allowClear
          onClear={() => {
            setInputValue('');
            onChange?.('', undefined);
            setShowTooltip(false);
          }}
        />
      </Tooltip>
    </div>
  );
};

export default AccountSelector;
